import { Box, Paper, Typography, Divider } from "@mui/material";
import { StripePaymentModule } from "src/common/components/StripePaymentModule";
import { PaytmPaymentModule } from "src/common/components/PaytmPaymentModule";


function formatMoney(amount, currency = "INR") {
  const symbol = currency === "INR" ? "₹" : "$";
  return `${symbol}${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const Row = ({ label, value, bold = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
    <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 600 : 400}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 600 : 400}>
      {value}
    </Typography>
  </Box>
);

// plan.line_items is expected to be an array, but some callers (see
// PlanSelectionStep) can hand it through as a single object. Normalize
// here so this component never throws on .map().
function normalizeLineItems(lineItems) {
  if (Array.isArray(lineItems)) return lineItems;
  if (lineItems) return [lineItems];
  return [];
}

const OrderSummary = ({ plan, account }) => {
  const billingLabel = plan.duration === "Y" ? "/year" : plan.duration === "M" ? "/month" : " one-time";
  const lineItems = normalizeLineItems(plan.line_items);

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: "100%" }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        Order Summary
      </Typography>

      {account && (
        <>
          <Row label="Name" value={account.name} />
          <Row label="Email" value={account.email} />
          {account.company && <Row label="Company" value={account.company} />}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {lineItems.map((item, index) => (
        <Row
          key={item.id ?? index}
          label={`${item.label} (${item.quantity})`}
          value={formatMoney(item.line_total, plan.currency)}
        />
      ))}

      <Divider sx={{ my: 1.5 }} />
      <Row label="Total" value={`${formatMoney(plan.total_cost, plan.currency)}${billingLabel}`} bold />
    </Paper>
  );
};

/**
 * Props:
 * - plan: payload from PlanSelectionStep ({ line_items, total_cost, currency, duration })
 * - account: { name, email, company }
 * - onBack: () => void
 * - returnUrl: where Stripe redirects on success
 * - onboarding: response from initiateOnboardingPayment API. Shape:
 *     Stripe -> { for: "stripe", clientId, paymentId, orderId }
 *     Paytm  -> { for: "paytm", txnToken, orderId, merchantId }
 *   The gateway (`for`) is decided by the backend based on the user's
 *   country, so there's no manual toggle here — whichever module matches
 *   `onboarding.for` is rendered.
 *
 * Payment is handled entirely by the existing StripePaymentModule and
 * PaytmPaymentModule (same ones used in ChargerList).
 */
export default function CheckoutPage({ plan, account, onBack, returnUrl, onboarding }) {
  const resolvedReturnUrl = returnUrl || `${window.location.origin}/payment-completion`;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: { xs: "40px 16px", md: "60px 32px" },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 960,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.2fr" },
          gap: 3,
        }}
      >
        <OrderSummary plan={plan} account={account} />

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <StripePaymentModule
            open={onboarding?.for === "stripe"}
            onClose={() => onBack?.()}
            clientId={onboarding?.clientId}
            paymentId={onboarding?.paymentId}
            returnUrl={onboarding?.return_url || resolvedReturnUrl}
            onError={() => onBack?.()}
          />

          <PaytmPaymentModule
            open={onboarding?.for === "paytm"}
            onClose={() => onBack?.()}
            txnId={onboarding?.txnToken}
            orderId={onboarding?.orderId}
            merchantId={onboarding?.merchantId}
            amount={onboarding?.amount ?? plan.total_cost}
            onError={() => onBack?.()}
          />
        </Paper>
      </Box>
    </Box>
  );
}